import type { Locale } from '../i18n';

export type UserView = {
  id: string;
  email: string;
  emailVerified: boolean;
  displayName?: string;
  avatarUrl?: string;
  locale: string;
  role: 'user' | 'partner' | 'admin';
};

export type SessionResponse = {
  accessToken: string;
  expiresAt: string;
  user: UserView;
};

export type Fingerprint = {
  userAgent: string;
  platform: string;
  screen: string;
  timezone: string;
  language: string;
  colorDepth: string;
  hwConcurrency: string;
  deviceMemory: string;
  canvas: string;
};

export function collectFingerprint(): Fingerprint {
  const nav = navigator;
  const scr = screen;
  return {
    userAgent: nav.userAgent || '',
    platform: (nav as any).platform || '',
    screen: `${scr.width}x${scr.height}x${scr.pixelDepth}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    language: nav.language || '',
    colorDepth: String(scr.colorDepth || ''),
    hwConcurrency: String(nav.hardwareConcurrency || ''),
    deviceMemory: String((nav as any).deviceMemory || ''),
    canvas: '',
  };
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status}`;
    try {
      const body = await res.json();
      msg = body?.error?.message || msg;
    } catch {}
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export async function requestMagicLink(
  email: string,
  locale: Locale,
  fingerprint: Fingerprint,
  opts: { partnerOnly?: boolean } = {},
) {
  const res = await fetch('/api/auth/magic-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, locale, fingerprint, partnerOnly: !!opts.partnerOnly }),
  });
  return handle<{ ok: true }>(res);
}

export async function verifyMagicLink(token: string, fingerprint: Fingerprint) {
  const res = await fetch('/api/auth/magic-link/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, fingerprint }),
  });
  return handle<SessionResponse>(res);
}

// Verify the 6-digit code from the sign-in email — the typeable alternative
// to clicking the link. Same session result; works when the user is reading
// mail on a different device or doesn't want to click through.
export async function verifyMagicLinkCode(
  email: string,
  code: string,
  fingerprint: Fingerprint,
) {
  const res = await fetch('/api/auth/magic-link/verify-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, code, fingerprint }),
  });
  return handle<SessionResponse>(res);
}

export async function refresh() {
  const res = await fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  });
  return handle<SessionResponse>(res);
}

export async function logout() {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
}

export async function me(accessToken: string) {
  const res = await fetch('/api/me', {
    headers: { Authorization: `Bearer ${accessToken}` },
    credentials: 'include',
  });
  return handle<{ user: UserView }>(res);
}

export type DeleteAccountResponse = {
  ok: boolean;
  deletedAt: string;
  graceDays: number;
};

/**
 * Soft-deletes the current user. The server requires `{confirm: "DELETE"}`
 * as a fat-finger guard. After this call the session is revoked — the
 * UI should drop the access token and bounce to the public landing.
 */
export async function deleteAccount(accessToken: string) {
  const res = await fetch('/api/me', {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ confirm: 'DELETE' }),
  });
  return handle<DeleteAccountResponse>(res);
}
